/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDebounce } from 'react-use';

import {
  selectPlan,
  selectResourceGroupIdForPremiumPlan
} from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { getTenant } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { LoaderStates } from '@ibm-watson/shared/ts';
import { IntegrationSchema } from '@ibm-watson/shared/ts/integration';
import { subscriptionType } from '../components/phone/phone-constants';
import { FIELDS } from '../components/phone/schema';
import { ConnectStepMessages as stepMessages } from '../components/segment/connect-step-tab/messages';
import {
  createIntegration,
  subscribeIntegration
} from '../store/channels/actions';
import {
  createIntegration as oldCreateIntegration,
  loadCurrentIntegration as oldLoadCurrentIntegration
} from '../store/channels/classic/actions';
import {
  defineNewIntegration,
  updateDraftIntegrationName,
  updateDraftIntegrationType
} from '../store/channels/reducer';
import {
  isCreatingIntegration,
  isSubscribeIntegrationLoading,
  selectDraftIntegration
} from '../store/channels/selectors';
import { selectContactCenterConnectionStatus } from '../store/phone/selectors';
import { validateSegmentKey } from '../store/segment/actions';
import {
  selectIsSegmentKeyValid,
  selectSegmentKeyValue
} from '../store/segment/selectors';
import { connectContactCenter } from '../store/service-desks/actions';
import { CATALOG_CHANNEL_TYPE } from '../util/channelTypes';
import { oneCloudUrl } from '../util/urls';
import { useFormHelpers } from './useFormHelpers';

/**
 * useChannelHelpers
 *   Provides functions to act on an existing established react-hook-form
 * @param {object} formMethods - result of useForm or useFormContext react-form-hooks
 * @returns {object}
 */
export const useChannelHelpers = formMethods => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();
  const { register, trigger } = formMethods;
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const draftIntegration = useAppSelector(selectDraftIntegration);
  const isCreateLoading = useAppSelector(isCreatingIntegration);
  const isSubscribeLoading = useAppSelector(isSubscribeIntegrationLoading);
  const contactCenterConnectionStatus = useAppSelector(
    selectContactCenterConnectionStatus
  );
  const plan = useAppSelector(selectPlan);
  const premiumPlanIntegrationOrgId = useAppSelector(
    selectResourceGroupIdForPremiumPlan
  );
  const region = useAppSelector(getTenant)?.region;

  /**
   * use to check if its a valid segment key.
   * If value is enter, dispatch an action which makes an api
   * call to see the validity of the entered value key.
   * Display appropriate message if its invalid.
   * Don't make an api call when its readOnly.
   * (internal)
   */
  const useValidSegmentKey = (field, readOnly) => {
    const value = getFieldValue(field);
    const valueEmpty = _.isEmpty(value);
    const key = useAppSelector(selectSegmentKeyValue);
    const validSegmentKey = useAppSelector(selectIsSegmentKeyValid);
    const isSameKey = _.isEqual(key, value);

    useDebounce(
      () => {
        if (!valueEmpty && !isSameKey && !readOnly) {
          dispatch(validateSegmentKey(value));
        }
      },
      500,
      [value]
    );

    return {
      name: field,
      id: field,
      invalid: !readOnly && !validSegmentKey,
      invalidText: t(stepMessages.invalidKey),
      ...register(field)
    };
  };

  const setNewIntegration = useCallback(
    integration => {
      // Don't update if not defined
      if (integration.name && integration.type) {
        dispatch(
          defineNewIntegration({
            integration,
            settingsIsValid: integration.type !== CATALOG_CHANNEL_TYPE
          })
        );
      }
    },
    [dispatch]
  );

  const updateIntegrationName = useCallback(
    (value, isValid = true, isDirty = true) => {
      dispatch(
        updateDraftIntegrationName({
          value,
          isValid,
          isDirty
        })
      );
    },
    [dispatch]
  );

  const updateIntegrationType = useCallback(
    value => {
      dispatch(
        updateDraftIntegrationType({
          value
        })
      );
    },
    [dispatch]
  );

  const getDraftDefaultWebhookURL = useCallback(
    () =>
      oneCloudUrl(draftIntegration, plan, premiumPlanIntegrationOrgId, region),
    [draftIntegration, plan, premiumPlanIntegrationOrgId, region]
  );

  const subscribeForWebhookUrl = useCallback(
    async (
      assistantId: string,
      environmentId: string | undefined,
      integration: IntegrationSchema,
      data: unknown,
      webhookUrlField: string,
      overrideChannelType?: string
    ) => {
      if (isCreateLoading || isSubscribeLoading) {
        return;
      }

      let integrationId;
      if (_.isNil(integration?.integration_id)) {
        // Create the integration in new IA
        if (!_.isNil(environmentId)) {
          const createdIntegration = await dispatch(
            createIntegration({
              assistantId,
              environmentId,
              integration,
              // Mark that this integration is created prematurely,
              // meaning that Tanya has NOT clicked finished in
              // ChannelOverlay. If Tanya discards changes before
              // finishing, then we need to delete this integration.
              markAsPremature: true
            })
          ).unwrap();
          integrationId = createdIntegration?.integration_id;
        } else {
          // Or create it in old IA.
          const createdIntegration = await dispatch(
            oldCreateIntegration({
              assistantId,
              integration,
              // Mark that this integration is created prematurely,
              // meaning that Tanya has NOT clicked finished in
              // ChannelOverlay. If Tanya discards changes before
              // finishing, then we need to delete this integration.
              markAsPremature: true
            })
          ).unwrap();
          integrationId = createdIntegration?.integration_id;
          // Load the new integration.
          await dispatch(
            oldLoadCurrentIntegration({
              assistantId,
              integrationId,
              mergeDraft: true
            })
          );
        }
      }

      const webhookSubscribeResponse = await dispatch(
        subscribeIntegration({
          channelType: overrideChannelType ?? integration.type,
          integrationId: integrationId ?? integration?.integration_id,
          data
        })
      );
      const url = webhookSubscribeResponse?.payload?.webhook_url;
      updateFieldValue(webhookUrlField, url);
      trigger();
    },
    [dispatch, isCreateLoading, isSubscribeLoading, updateFieldValue, trigger]
  );

  /**
   * Use to test NICE CXONE or Amazon Connect connection
   */
  const connectToContactCenter = async (type: string) => {
    if (contactCenterConnectionStatus !== LoaderStates.ACTIVE) {
      const isAmazonConnect = type === subscriptionType.AMAZON_CONNECT;

      // For some reason, service desk backend requires the phone
      // connection type names to be slightly different than their
      // default names.
      let adjustedType = type;
      if (type === subscriptionType.NICE_CXONE) {
        adjustedType = subscriptionType.NICE_CXONE_SUBSCRIBE;
      }
      if (type === subscriptionType.AMAZON_CONNECT) {
        adjustedType = subscriptionType.AMAZON_CONNECT_SUBSCRIBE;
      }

      const data = isAmazonConnect
        ? {
            access_key_id: _.get(
              draftIntegration?.settings,
              FIELDS.CONTACT_CENTER.ACCESS_KEY_ID
            ),
            access_key_secret: _.get(
              draftIntegration?.settings,
              FIELDS.CONTACT_CENTER.ACCESS_KEY_SECRET
            ),
            arn: _.get(draftIntegration?.settings, FIELDS.CONTACT_CENTER.ARN),
            type: adjustedType,
            integrationId: draftIntegration?.integration_id
          }
        : {
            access_key_id: _.get(
              draftIntegration?.settings,
              FIELDS.CONTACT_CENTER.ACCESS_KEY_ID
            ),
            access_key_secret: _.get(
              draftIntegration?.settings,
              FIELDS.CONTACT_CENTER.ACCESS_KEY_SECRET
            ),
            type: adjustedType,
            integrationId: draftIntegration?.integration_id
          };
      await dispatch(connectContactCenter(data));
    }
  };

  /**
   * Use to get connectURI for GenesysAudioConnector
   */
  const getConnectURI = async (
    type: string,
    apiKey: string,
    clientSecret: string
  ) => {
    if (contactCenterConnectionStatus !== LoaderStates.ACTIVE) {
      // For some reason, service desk backend requires the phone
      // connection type names to be slightly different than their
      // default names.
      const adjustedType = subscriptionType.GENESYS_AUDIO_CONNECTOR_SUBSCRIBE;

      const data = {
        api_key: apiKey,
        client_secret: clientSecret,
        type: adjustedType,
        integrationId: draftIntegration?.integration_id
      };

      const response = await dispatch(connectContactCenter(data));
      const url = response?.payload?.connect_url;
      updateFieldValue(FIELDS.GENESYS_AUDIO_CONNECTOR.CONNECT_URL, url);
    }
  };

  return {
    getDraftDefaultWebhookURL,
    updateIntegrationName,
    updateIntegrationType,
    subscribeForWebhookUrl,
    setNewIntegration,
    useValidSegmentKey,
    connectToContactCenter,
    getConnectURI
  };
};
